import axios from 'axios';
import { API_URL } from './apiConfig';

export interface LearningMaterial {
  id: number;
  title: string;
  content: string;
  description?: string;
  lessonId?: number;
  order: number;
  isPublic: boolean;
  createdById: number;
  createdBy?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  sections?: LearningMaterialSection[];
  materialMedia?: Array<{
    mediaId: number;
    order: number;
    placement: string;
    caption?: string;
    width?: string;
    rotation?: number;
    media: Media;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface LearningMaterialSection {
  id: number;
  materialId?: number;
  title?: string | null;
  content: string;
  order: number;
  sectionMedia?: Array<{
    mediaId: number;
    order: number;
    placement: string;
    caption?: string;
    width?: string;
    rotation?: number;
    media: Media;
  }>;
}

export interface Media {
  id: number;
  fileName: string;
  originalFileName: string;
  fileType: 'image' | 'video' | 'audio';
  mimeType: string;
  fileSize: number;
  blobUrl: string;
  thumbnailUrl?: string;
  duration?: number;
  width?: number;
  height?: number;
  altText?: string;
  uploadedById: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLearningMaterialDto {
  title: string;
  content?: string;
  description?: string;
  lessonId?: number;
  order?: number;
  isPublic?: boolean;
  mediaAttachments?: MaterialMediaAttachmentInput[];
  sections?: MaterialSectionInput[];
}

export interface UpdateLearningMaterialDto {
  title?: string;
  content?: string;
  description?: string;
  order?: number;
  isPublic?: boolean;
  mediaAttachments?: MaterialMediaAttachmentInput[];
  sections?: MaterialSectionInput[];
}

export interface MaterialMediaAttachmentInput {
  mediaId: number;
  order?: number;
  placement?: 'inline' | 'top' | 'bottom' | 'left' | 'right' | 'gallery';
  caption?: string;
  width?: string;
  rotation?: number;
}

export interface MaterialSectionInput {
  title?: string;
  content?: string;
  order?: number;
  mediaAttachments?: MaterialMediaAttachmentInput[];
}

const learningMaterialService = {
  async getMaterialsByLesson(lessonId: number): Promise<LearningMaterial[]> {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/learningmaterial/lesson/${lessonId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data;
  },

  async getMaterial(id: number): Promise<LearningMaterial> {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/learningmaterial/${id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data;
  },

  async createMaterial(data: CreateLearningMaterialDto): Promise<LearningMaterial> {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/api/learningmaterial`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data;
  },

  async updateMaterial(id: number, data: UpdateLearningMaterialDto): Promise<LearningMaterial> {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${API_URL}/api/learningmaterial/${id}`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data;
  },

  async deleteMaterial(id: number): Promise<void> {
    const token = localStorage.getItem('token');
    await axios.delete(
      `${API_URL}/api/learningmaterial/${id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  },

  async uploadMedia(file: File): Promise<Media> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_URL}/api/media/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  async extractTextFromImage(file: File | Blob): Promise<string> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('image', file, file instanceof File ? file.name : 'material-image.png');

    const response = await axios.post(
      `${API_URL}/api/material/extractfromimage`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    return response.data.data as string;
  },

  async getMedia(id: number): Promise<Media> {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/media/${id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data;
  },

  async deleteMedia(id: number): Promise<void> {
    const token = localStorage.getItem('token');
    await axios.delete(
      `${API_URL}/api/media/${id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  },
};

export default learningMaterialService;
